import { API_URL } from "../config";

type ClientLogLevel = "debug" | "info" | "warn" | "error";

interface ClientLogContext {
  [key: string]: unknown;
}

interface ClientLogPayload {
  level: ClientLogLevel;
  event: string;
  message: string;
  timestamp: string;
  page?: string;
  referrer?: string;
  user_agent?: string;
  context: ClientLogContext;
}

const CLIENT_LOG_ENDPOINT = `${API_URL}/api/logs/client`;

function sendLogToServer(payload: ClientLogPayload) {
  if (typeof window === "undefined") {
    return;
  }

  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      CLIENT_LOG_ENDPOINT,
      new Blob([body], { type: "application/json" }),
    );
    return;
  }

  void fetch(CLIENT_LOG_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

export function logClientEvent(
  level: ClientLogLevel,
  event: string,
  message: string,
  context: ClientLogContext = {},
) {
  const payload: ClientLogPayload = {
    level,
    event,
    message,
    timestamp: new Date().toISOString(),
    page: typeof window !== "undefined" ? window.location.pathname : undefined,
    referrer: typeof document !== "undefined" ? document.referrer : undefined,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    context,
  };

  const consoleMethod = console[level] ?? console.log;
  consoleMethod(message, payload);

  if (level === "warn" || level === "error") {
    sendLogToServer(payload);
  }

  return payload;
}

export function logClientError(
  event: string,
  message: string,
  context: ClientLogContext = {},
) {
  return logClientEvent("error", event, message, context);
}

export function logClientInfo(
  event: string,
  message: string,
  context: ClientLogContext = {},
) {
  return logClientEvent("info", event, message, context);
}
